import { useId, useMemo } from "react";
import classNames from "classnames";
import { useFormikContext } from "formik";
import { useTaskSummaries } from "@api/task.api";
import { pluralizeWithCount } from "@common/helpers/string";
import { TaskCreate } from "@common/contracts/task.contract";
import { DescriptionListItem } from "@design-system/DescriptionList/DescriptionList";
import { Picker } from "@design-system/Picker/Picker.lib";
import fieldStyles from "./TaskDrawerFields.module.scss";

type TaskRelationName =
    | "blockedByTaskIds"
    | "blockingTaskIds"
    | "relatedTaskIds";

const EXCLUDED_RELATION_NAME: Partial<
    Record<TaskRelationName, TaskRelationName>
> = {
    blockedByTaskIds: "blockingTaskIds",
    blockingTaskIds: "blockedByTaskIds",
};

interface TaskRelationFieldProps {
    name: TaskRelationName;
    label: string;
}

export const TaskRelationField = ({ name, label }: TaskRelationFieldProps) => {
    const labelId = useId();
    const formik = useFormikContext<TaskCreate>();
    const { data: taskSummaries = [] } = useTaskSummaries();

    const selectedIds = useMemo(
        () => formik.values[name] ?? [],
        [formik.values, name],
    );
    const excludedRelationName = EXCLUDED_RELATION_NAME[name];
    const excludedIds = excludedRelationName
        ? formik.values[excludedRelationName]
        : undefined;

    const items = useMemo(
        () =>
            taskSummaries
                .filter((task) => !excludedIds?.includes(task.id))
                .map((task) => ({ value: task.id, label: task.name })),
        [taskSummaries, excludedIds],
    );

    const renderValue = (ids: readonly string[]) => {
        if (ids.length === 1) {
            return (
                taskSummaries.find((task) => task.id === ids[0])?.name ??
                pluralizeWithCount("task", 1)
            );
        }
        return pluralizeWithCount("task", ids.length);
    };

    return (
        <DescriptionListItem label={<span id={labelId}>{label}</span>}>
            <div className={fieldStyles.detailSelectWrapper}>
                <Picker.Select
                    multiple
                    searchable
                    items={items}
                    value={selectedIds}
                    onValueChange={(ids) => formik.setFieldValue(name, ids)}
                    placeholder="Add task"
                    searchPlaceholder="Search tasks…"
                    emptyLabel="No tasks found"
                    renderValue={renderValue}
                    chevron="none"
                    trigger={
                        <Picker.Button
                            aria-labelledby={labelId}
                            variant="primary"
                            size="md"
                            block={false}
                            className={classNames(
                                fieldStyles.detailSelectTrigger,
                                fieldStyles.justifyLeft,
                                {
                                    [fieldStyles.emptyValue]:
                                        selectedIds.length === 0,
                                },
                            )}
                        />
                    }
                />
            </div>
        </DescriptionListItem>
    );
};
